import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { bowser } from '@yola/ws-sdk';
import AiTextAssistant from '../components/ai-text-assistant';

const ENTER_KEYCODE = 13;
const SPACE_KEYCODE = 32;
const USER_INPUT_ID = 'ai-text-assistant-user-input';

const filterAutocompleteOptions = ({ label }, query) =>
  label.toLowerCase().includes(query.toLowerCase());

const defaultAutocompleteOptions = [];

const AiTextAssistantContainer = ({
  captions,
  autocompleteOptions = defaultAutocompleteOptions,
  isLoading = false,
  onSelect,
  onSubmit,
  onRetry,
  onCancel = Function.prototype,
  position,
  selectedAiRewriteOption = null,
}) => {
  const [userInput, setUserInput] = useState('');
  const [isRetryAvailable, setRetryStatus] = useState(false);
  const isMobile = bowser.mobile;

  const handleInputChange = (value) => {
    if (value === userInput) {
      return;
    }

    setUserInput(value);
    setRetryStatus(false);
  };

  const handleSubmit = () => {
    onSubmit(userInput);
    setRetryStatus(true);
  };

  const handleSelect = (option) => {
    if (!option?.value) {
      return;
    }

    const { value, label } = option;

    setUserInput(label);
    const { withRetry = true } = onSelect(value);
    setRetryStatus(withRetry);
  };

  const handleKeyDown = (event, details) => {
    const { focusedOption } = details;

    if (focusedOption && event.keyCode === SPACE_KEYCODE && !userInput.trim()) {
      event.preventDefault();
    }

    if (
      event.keyCode !== ENTER_KEYCODE ||
      focusedOption ||
      document.activeElement?.id !== USER_INPUT_ID ||
      !userInput.trim() ||
      isLoading
    ) {
      setRetryStatus(false);
      return;
    }

    handleSubmit();
  };

  const iconButtonProps = isRetryAvailable
    ? {
        iconGlyph: 'reset',
        onClick: onRetry,
      }
    : {
        iconGlyph: 'paper-plane',
        onClick: handleSubmit,
      };

  return (
    <AiTextAssistant
      captions={captions}
      autocompleteOptions={autocompleteOptions}
      isMobile={isMobile}
      isLoading={isLoading}
      isRetryAvailable={isRetryAvailable}
      userInputId={USER_INPUT_ID}
      userInput={userInput}
      actionButtonProps={iconButtonProps}
      filterAutocompleteOptions={filterAutocompleteOptions}
      onChange={handleSelect}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onCancel={onCancel}
      position={position}
      selectedAiRewriteOption={selectedAiRewriteOption}
    />
  );
};

AiTextAssistantContainer.propTypes = {
  captions: PropTypes.shape({
    placeholder: PropTypes.string.isRequired,
    loading: PropTypes.string.isRequired,
    cancel: PropTypes.string.isRequired,
    noResultsText: PropTypes.string,
    inputPrefix: PropTypes.string,
  }).isRequired,
  autocompleteOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.shape(),
      formatLabel: PropTypes.func,
    })
  ),
  isLoading: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  position: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    width: PropTypes.number,
  }).isRequired,
  selectedAiRewriteOption: PropTypes.shape({}),
};

export default AiTextAssistantContainer;
